import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Table,
  Tabs,
  Button,
  Input,
  Alert,
  Spin,
  message,
  Progress,
  Tag,
  Space,
} from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import axiosInstance from "../../../utils/axiosInstance";
import { LOCALE_LIST } from "../../../constants/LocaleList";
import { DMA_LIST } from "../../../constants/DMAList";

const { TabPane } = Tabs;

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google Ads",
  meta: "Meta Ads",
  universal_ads: "Universal Ads",
};

// Shared so every place that needs to know "what does this cell actually
// show" - the column filter options/onFilter, and the filter-driven
// selection logic (Select All, handleTableChange, removeFilter) - treats a
// value identically. Notably Coverage: its filter/tag values are the
// rendered "45.23%" percent text, not the raw unrounded record value, so
// matching against the raw value would silently match nothing.
const formatCoveragePercent = (v: any): string =>
  typeof v === "number" || !isNaN(parseFloat(v)) ? `${parseFloat(v).toFixed(2)}%` : String(v);

const getColumnDisplayValue = (columnKey: string, record: any): string =>
  columnKey === "Coverage" ? formatCoveragePercent(record[columnKey]) : String(record[columnKey]);

interface StepApplyBidProps {
  jobConfigId: string;
  onNext: () => void;
  onPrevious: () => void;
}

// This step is the real Bid Optimizer "Results" page's logic, forked (not
// imported) into a Causal-Loop-owned copy - the table, its filters/selection,
// and the Facebook/Google apply flow are identical to
// optimization/bid-optimizer/components/ResultStep.tsx. It's a fork rather
// than a direct reuse because Causal Loop needs changes ResultStep.tsx itself
// must not get (a Universal Ads tab, no "Bids Result" card title, and header
// styling consistent with the rest of this wizard) - editing ResultStep.tsx
// in place would affect the standalone Bid Optimizer feature. See
// causal-loop-feature/progress.md.
const StepApplyBid: React.FC<StepApplyBidProps> = ({ jobConfigId, onNext, onPrevious }) => {
  const [bidsColumns, setBidsColumns] = useState<any[]>([]);
  const [bidsRecords, setBidsRecords] = useState<any[]>([]);
  const [bidsJson, setBidsJson] = useState<any>();
  const [jobConfigData, setJobConfigData] = useState<any>();
  const [appliedBids, setAppliedBids] = useState<any[]>([]);
  const [isAlert, setAlert] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("facebook");
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [universalLoading, setUniversalLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const [filterState, setFilterState] = useState<Record<string, string[] | null>>({});
  const [selectedFilters, setSelectedFilters] = useState<
    {
      column: string;
      value: string;
      key: string;
    }[]
  >([]);
  // Causal-Loop-only: no Control row - this study's "control" is the untreated
  // markets Geo Lift already measures against, not a second ad-platform
  // campaign, so only a Test campaign/budget is collected per provider.
  const [formData, setFormData] = useState({
    testAdsetId: "",
    testBudget: "",
    googleTestCampaignId: "",
    googleTestBudget: "",
    universalTestCampaignId: "",
    universalTestBudget: "",
  });

  // Causal-Loop-only: only show segment rows for markets actually selected in
  // Step 2's Geo Test Setup (and carried through Power Analysis) - the raw
  // Bid Optimizer output has segments for every region in the uploaded file,
  // but this study is only about the markets chosen for the geo test.
  // Filtering here (display only) rather than upstream keeps bidsJson/indices
  // untouched, so selectedRowKeys still correctly map back to the full
  // bid-multiplier array when building the apply payload below.
  const regionColumn: string | undefined =
    jobConfigData?.dataMappingConfig?.mappings?.bidMultiplier?.columnMapping?.region_varialbes?.[0];
  const selectedMarketsLower = useMemo(
    () =>
      (jobConfigData?.modelParameterConfig?.parameters?.includeMarket || []).map((m: string) =>
        String(m).trim().toLowerCase()
      ),
    [jobConfigData]
  );
  // Grouped by location (all of one market's rows together, in the order
  // markets were selected in Step 2), rather than interleaved by Bid Value -
  // easier to review one market at a time. Within a location, rows keep the
  // existing highest-Bid-Value-first order.
  const visibleBidsRecords = useMemo(() => {
    const filtered =
      !regionColumn || selectedMarketsLower.length === 0
        ? bidsRecords
        : bidsRecords.filter((r) =>
            selectedMarketsLower.includes(String(r[regionColumn] ?? "").trim().toLowerCase())
          );
    if (!regionColumn) return filtered;
    return [...filtered].sort((a, b) => {
      const locA = String(a[regionColumn] ?? "").trim().toLowerCase();
      const locB = String(b[regionColumn] ?? "").trim().toLowerCase();
      const idxA = selectedMarketsLower.indexOf(locA);
      const idxB = selectedMarketsLower.indexOf(locB);
      if (idxA !== idxB) return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
      return (parseFloat(b["Bid Value"]) || 0) - (parseFloat(a["Bid Value"]) || 0);
    });
  }, [bidsRecords, regionColumn, selectedMarketsLower]);

  // Causal-Loop-only: build each column's filter options from
  // visibleBidsRecords (the market-filtered, already-formatted rows the table
  // actually renders) instead of the raw all-markets bid-multiplier data -
  // otherwise the location filter offered markets that aren't even shown in
  // the table, and numeric columns like Coverage (a raw float with full
  // precision, unlike Bid Value which was already rounded here) produced a
  // near-unique filter entry per row. displayValue() mirrors each column's
  // actual cell text (percent-formatted for Coverage) so onFilter matches
  // what the filter list shows. Also pushes "Coverage" to the very end, after
  // "Bid Value" - the shared sort in the generic column-builder above only
  // special-cases "Bid Value" to be last, so without this, Coverage would
  // land just before it instead. Only reorders/reformats this component's
  // own column list; ResultStep.tsx's identical generic builder is untouched.
  const columnsWithFilters = useMemo(() => {
    const coverageIdx = bidsColumns.findIndex((c: any) => c.key === "Coverage");
    const orderedCols =
      coverageIdx === -1
        ? bidsColumns
        : [
            ...bidsColumns.slice(0, coverageIdx),
            ...bidsColumns.slice(coverageIdx + 1),
            bidsColumns[coverageIdx],
          ];

    return orderedCols.map((col: any) => {
      const isBidValue = col.key === "Bid Value";
      const isCoverage = col.key === "Coverage";

      const uniqueValues = Array.from(
        new Set(visibleBidsRecords.map((record) => getColumnDisplayValue(col.key, record)))
      );
      const filters = uniqueValues
        .map((value) => ({ text: value, value }))
        .sort((a, b) => (isBidValue ? parseFloat(b.value) - parseFloat(a.value) : a.text.localeCompare(b.text)));

      return {
        ...col,
        ...(isCoverage ? { title: "Coverage", render: (v: any) => formatCoveragePercent(v) } : {}),
        filters,
        onFilter: (value: string, record: any) => getColumnDisplayValue(col.key, record) === value,
        filteredValue: filterState[col.key] ?? null,
      };
    });
  }, [bidsColumns, filterState, visibleBidsRecords]);

  // Shared by Select All and the "keep the selection in sync with active
  // filters" logic in handleTableChange/removeFilter - matches against
  // visibleBidsRecords (every filtered-in row, not just the current page's
  // slice), so Select All picks up filtered rows across all pages.
  const getKeysMatchingFilters = (filters: Record<string, string[] | null | undefined>) => {
    const activeEntries = Object.entries(filters).filter(
      (entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].length > 0
    );
    const matchingRows =
      activeEntries.length === 0
        ? visibleBidsRecords
        : visibleBidsRecords.filter((record) =>
            activeEntries.every(([column, values]) =>
              values.some((value) => getColumnDisplayValue(column, record) === value)
            )
          );
    return matchingRows.map((record) => record.key);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch bid multiplier output data — may not be ready yet if the job
        // is still running.
        let outputJson;
        let outputUnavailable = false;
        try {
          const outputResponse = await axiosInstance.get(
            `/api/bid-multiplier/output/${jobConfigId}`
          );
          outputJson = outputResponse.data.result;
          if (!outputJson) outputUnavailable = true;
        } catch (apiError) {
          outputUnavailable = true;
        }

        const processData = (data: any) => {
          if (
            !data["bid-multiplier"] ||
            !Array.isArray(data["bid-multiplier"]) ||
            data["bid-multiplier"].length === 0
          ) {
            message.error("No bid multiplier data available");
            return false;
          }

          setBidsJson(data);

          const columns = Object.keys(data["bid-multiplier"][0])
            .filter((key) => key.trim() !== "Google Campaign Id")
            .sort((a, b) => {
              if (a === "Bid Value") return 1;
              if (b === "Bid Value") return -1;
              return 0;
            })
            .map((key) => {
              const trimmedKey = key.trim();
              const isBidValue = trimmedKey === "Bid Value";

              const columnTitle = isBidValue
                ? "Bid Multiplier"
                : trimmedKey.replaceAll("_", " ");

              // filters/onFilter are attached in columnsWithFilters below,
              // derived from visibleBidsRecords rather than this raw,
              // all-markets dataset - see that memo's comment.
              return {
                title: columnTitle,
                dataIndex: trimmedKey,
                key: trimmedKey,
                // No defaultSortOrder here (unlike the standalone Bid
                // Optimizer page this table's logic mirrors) - a
                // defaultSortOrder makes antd apply this column's own sorter
                // immediately, which would override visibleBidsRecords'
                // location-grouped base order. Users can still click the
                // column header to sort by Bid Value manually.
                sorter: (a: any, b: any) => {
                  const valueA = parseFloat(a[trimmedKey]);
                  const valueB = parseFloat(b[trimmedKey]);
                  if (!isNaN(valueA) && !isNaN(valueB)) {
                    return valueA - valueB;
                  }
                  return String(a[trimmedKey]).localeCompare(String(b[trimmedKey]));
                },
              };
            });

          setBidsColumns(columns);

          const records = data["bid-multiplier"].map(
            (item: any, index: number) => {
              const record: any = { key: index };
              Object.keys(item).forEach((key) => {
                const value = item[key];
                record[key.trim()] =
                  typeof value === "string" ? value.trim() : value.toFixed(2);
              });
              return record;
            }
          );

          records.sort((a: any, b: any) => {
            const aVal = parseFloat(a["Bid Value"]) || 0;
            const bVal = parseFloat(b["Bid Value"]) || 0;
            return bVal - aVal;
          });

          setSelectedRowKeys([]);
          setBidsRecords(records);
          return true;
        };

        if (outputUnavailable) {
          setIsProcessing(true);
        } else if (typeof outputJson === "string") {
          try {
            outputJson = JSON.parse(outputJson);
            processData(outputJson);
          } catch (error) {
            setIsProcessing(true);
          }
        } else if (outputJson && typeof outputJson === "object") {
          processData(outputJson);
        } else {
          setIsProcessing(true);
        }

        try {
          const configResponse = await axiosInstance.get(
            `/api/jobConfig/${jobConfigId}`
          );
          setJobConfigData(configResponse.data);
          setAppliedBids(configResponse.data?.modelOutputConfig?.outputData?.appliedBids || []);
        } catch (configError) {
          setJobConfigData({
            name: "Causal Loop Study",
            dataMappingConfig: {
              mappings: {
                bidMultiplier: {
                  columnMapping: {
                    age_varialbes: ["age"],
                    gender_varialbes: ["gender"],
                    locale_varialbes: ["locale"],
                    region_varialbes: ["region"],
                    device_platform_varialbes: ["device"],
                    user_devices_varialbes: [],
                    user_os_varialbes: [],
                  },
                },
              },
            },
            modelParameterConfig: {
              parameters: {
                geoLocationType: "region",
              },
            },
          });
        }
      } catch (error) {
        setIsProcessing(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobConfigId]);

  const getRegionKey = (region: any, provider: "google" | "facebook") => {
    const regionItem = DMA_LIST.find((item) => item.name === region);
    return provider === "facebook" ? regionItem?.key : regionItem?.gg_ads_key;
  };

  const getLocaleKey = (locale: any, provider: "google" | "facebook") => {
    const localeItem = LOCALE_LIST.find((item) => item.name === locale);
    return provider === "facebook" ? localeItem?.key : localeItem?.gg_locale;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Persists an apply-bids action to this job's own record (DB-only - no real
  // ad-platform push, same as the mocked /api/bid-multiplier/{id} call above)
  // so a later "View" of this study can show what was already applied.
  // Causal-Loop-only endpoint, built earlier for this exact purpose; unrelated
  // to and doesn't touch the standalone Bid Optimizer's (non-persisting)
  // apply flow this table's UI otherwise matches exactly. The backend record
  // still has controlCampaignId/controlBudget fields (unchanged schema) - sent
  // as empty/0 placeholders now that Causal Loop's UI no longer collects a
  // Control campaign at all.
  const persistAppliedBid = async (
    provider: "google" | "meta" | "universal_ads",
    testCampaignId: string,
    testBudget: number
  ) => {
    try {
      const response = await axiosInstance.post(`/api/causal-loop/${jobConfigId}/apply-bids`, {
        provider,
        controlCampaignId: "",
        controlBudget: 0,
        testCampaignId,
        testBudget,
        selectedRowIndices: selectedRowKeys.map((k) => Number(k)),
      });
      setAppliedBids((prev) => [...prev, response.data]);
    } catch (err) {
      console.error("Failed to save applied bids record:", err);
    }
  };

  const onApplyBids = async () => {
    if (!formData.testAdsetId || !formData.testBudget) {
      message.error("Please fill in all fields");
      return;
    }

    if (!jobConfigData || !bidsJson) {
      message.error("Missing required data");
      return;
    }

    if (selectedRowKeys.length === 0) {
      message.error("Please select at least one row to apply bids");
      return;
    }

    const columnMapping =
      jobConfigData.dataMappingConfig.mappings.bidMultiplier.columnMapping;

    const selectedBidData = bidsJson["bid-multiplier"].filter(
      (_: unknown, index: number) => selectedRowKeys.includes(index)
    );
    const payload = {
      jobConfigId: jobConfigId,
      provider: "facebookads",
      testAdsetId: formData.testAdsetId,
      testBudget: parseFloat(formData.testBudget),
      payload: selectedBidData.map((item: any) => ({
        age: item.age,
        devicePlatform: columnMapping?.device_platform_varialbes?.length
          ? item[columnMapping.device_platform_varialbes[0]]
          : undefined,
        gender: columnMapping?.gender_varialbes?.length
          ? item[columnMapping.gender_varialbes[0]]
          : undefined,
        locale: columnMapping?.locale_varialbes?.length
          ? getLocaleKey(item[columnMapping.locale_varialbes[0]], "facebook")
          : undefined,
        regions: columnMapping?.region_varialbes?.length
          ? getRegionKey(item[columnMapping.region_varialbes[0]], "facebook")
          : undefined,
        salesScaled: item["Bid Value"],
        userDevice: columnMapping?.user_devices_varialbes?.length
          ? item[columnMapping.user_devices_varialbes[0]]
          : undefined,
        userOS: columnMapping?.user_os_varialbes?.length
          ? item[columnMapping.user_os_varialbes[0]]
          : undefined,
      })),
    };

    setFacebookLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Facebook");
      handleAlert();
    } catch (error) {
      message.success("Bids successfully applied to Facebook");
      handleAlert();
    } finally {
      await persistAppliedBid("meta", formData.testAdsetId, parseFloat(formData.testBudget));
      setFacebookLoading(false);
    }
  };

  const onGoogleApplyBids = async () => {
    if (!formData.googleTestCampaignId || !formData.googleTestBudget) {
      message.error("Please fill in all fields");
      return;
    }

    if (!jobConfigData || !bidsJson) {
      message.error("Missing required data");
      return;
    }

    if (selectedRowKeys.length === 0) {
      message.error("Please select at least one row to apply bids");
      return;
    }

    const columnMapping =
      jobConfigData.dataMappingConfig.mappings.bidMultiplier.columnMapping;

    const selectedBidData = bidsJson["bid-multiplier"].filter(
      (_: unknown, index: number) => selectedRowKeys.includes(index)
    );

    const payload = {
      jobConfigId: jobConfigId,
      provider: "googleads",
      testCampaignId: formData.googleTestCampaignId,
      testBudget: parseFloat(formData.googleTestBudget),
      payload: selectedBidData.map((item: any) => ({
        age: columnMapping?.age_varialbes?.length
          ? item[columnMapping.age_varialbes[0]]
          : undefined,
        devicePlatform: columnMapping?.device_platform_varialbes?.length
          ? item[columnMapping.device_platform_varialbes[0]]
          : undefined,
        gender: columnMapping?.gender_varialbes?.length
          ? item[columnMapping.gender_varialbes[0]]
          : undefined,
        locale: columnMapping?.locale_varialbes?.length
          ? getLocaleKey(item[columnMapping.locale_varialbes[0]], "google")
          : undefined,
        regions: columnMapping?.region_varialbes?.length
          ? getRegionKey(item[columnMapping.region_varialbes[0]], "google")
          : undefined,
        salesScaled: item["Bid Value"],
        userDevice: columnMapping?.user_devices_varialbes?.length
          ? item[columnMapping.user_devices_varialbes[0]]
          : undefined,
        userOS: columnMapping?.user_os_varialbes?.length
          ? item[columnMapping.user_os_varialbes[0]]
          : undefined,
      })),
    };

    setGoogleLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Google");
      handleAlert();
    } catch (error) {
      message.success("Bids successfully applied to Google");
      handleAlert();
    } finally {
      await persistAppliedBid("google", formData.googleTestCampaignId, parseFloat(formData.googleTestBudget));
      setGoogleLoading(false);
    }
  };

  // Universal Ads has no dedicated key-mapping lists of its own (it's a
  // Google-run cross-network product) - reuses the Google campaign/budget
  // grid shape and the same "google" key lookups. Same mocked-apply behavior
  // as the other two providers since /api/bid-multiplier/{id} isn't a real
  // endpoint yet.
  const onUniversalApplyBids = async () => {
    if (!formData.universalTestCampaignId || !formData.universalTestBudget) {
      message.error("Please fill in all fields");
      return;
    }

    if (!jobConfigData || !bidsJson) {
      message.error("Missing required data");
      return;
    }

    if (selectedRowKeys.length === 0) {
      message.error("Please select at least one row to apply bids");
      return;
    }

    const columnMapping =
      jobConfigData.dataMappingConfig.mappings.bidMultiplier.columnMapping;

    const selectedBidData = bidsJson["bid-multiplier"].filter(
      (_: unknown, index: number) => selectedRowKeys.includes(index)
    );

    const payload = {
      jobConfigId: jobConfigId,
      provider: "universal_ads",
      testCampaignId: formData.universalTestCampaignId,
      testBudget: parseFloat(formData.universalTestBudget),
      payload: selectedBidData.map((item: any) => ({
        age: columnMapping?.age_varialbes?.length
          ? item[columnMapping.age_varialbes[0]]
          : undefined,
        devicePlatform: columnMapping?.device_platform_varialbes?.length
          ? item[columnMapping.device_platform_varialbes[0]]
          : undefined,
        gender: columnMapping?.gender_varialbes?.length
          ? item[columnMapping.gender_varialbes[0]]
          : undefined,
        locale: columnMapping?.locale_varialbes?.length
          ? getLocaleKey(item[columnMapping.locale_varialbes[0]], "google")
          : undefined,
        regions: columnMapping?.region_varialbes?.length
          ? getRegionKey(item[columnMapping.region_varialbes[0]], "google")
          : undefined,
        salesScaled: item["Bid Value"],
        userDevice: columnMapping?.user_devices_varialbes?.length
          ? item[columnMapping.user_devices_varialbes[0]]
          : undefined,
        userOS: columnMapping?.user_os_varialbes?.length
          ? item[columnMapping.user_os_varialbes[0]]
          : undefined,
      })),
    };

    setUniversalLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Universal Ads");
      handleAlert();
    } catch (error) {
      message.success("Bids successfully applied to Universal Ads");
      handleAlert();
    } finally {
      await persistAppliedBid(
        "universal_ads",
        formData.universalTestCampaignId,
        parseFloat(formData.universalTestBudget)
      );
      setUniversalLoading(false);
    }
  };

  const handleAlert = () => {
    setAlert(true);
    setTimeout(() => {
      setAlert(false);
    }, 3000);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // Selects every row currently present in the table under the active
  // column filters (across all pages, not just the visible page slice) - or
  // every visible-market row when no filter is applied.
  const handleSelectAll = () => {
    setSelectedRowKeys(getKeysMatchingFilters(filterState));
  };

  const handleClearAll = () => {
    setSelectedRowKeys([]);
    setSelectedFilters([]);
    setFilterState({});
    setTableKey((prev) => prev + 1);
  };

  const handleTableChange = (pagination: any, filters: any) => {
    setFilterState(filters);

    const newFilters: { column: string; value: string; key: string }[] = [];

    Object.entries(filters).forEach(([column, values]) => {
      if (values && Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          newFilters.push({
            column,
            value: String(value),
            key: `${column}-${value}`,
          });
        });
      }
    });

    setSelectedFilters(newFilters);

    if (newFilters.length > 0) {
      setSelectedRowKeys(getKeysMatchingFilters(filters));
    }
  };

  const removeFilter = (filterKey: string) => {
    const filterToRemove = selectedFilters.find((f) => f.key === filterKey);
    const updatedFilters = selectedFilters.filter((f) => f.key !== filterKey);
    setSelectedFilters(updatedFilters);

    let updatedFilterState = filterState;
    if (filterToRemove) {
      updatedFilterState = { ...filterState };
      const colValues = (updatedFilterState[filterToRemove.column] || []).filter(
        (v) => v !== filterToRemove.value
      );
      updatedFilterState[filterToRemove.column] = colValues.length > 0 ? colValues : null;
      setFilterState(updatedFilterState);
    }

    setSelectedRowKeys(updatedFilters.length > 0 ? getKeysMatchingFilters(updatedFilterState) : []);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-full py-16">
        <Spin size="large" />
        <div className="mt-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Loading Bid Optimizer Results
          </h3>
          <p className="text-sm text-gray-600">
            Please wait while we fetch your optimization results...
          </p>
        </div>
        <div className="mt-8 w-1/2">
          <Progress
            percent={100}
            status="active"
            strokeColor={{
              "0%": "#108ee9",
              "100%": "#87d068",
            }}
          />
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="flex flex-col space-y-6">
        <Card className="shadow-sm">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-6 flex items-center justify-center w-20 h-20 rounded-full bg-blue-50">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Test Running in Background
            </h3>
            <p className="text-sm text-gray-600 max-w-md mb-2">
              Your bid optimization test has been submitted and is still
              processing. This usually takes a few minutes.
            </p>
            <p className="text-sm text-gray-400">
              Click <strong>Previous</strong> and come back shortly, or check
              the Causal Loop list once the job completes.
            </p>
          </div>
        </Card>
        <div className="flex justify-between">
          <Button size="large" onClick={onPrevious}>
            Previous
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <Card className="shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Bid Optimizer Results</h3>
        <p className="text-sm text-gray-600 mb-6">
          Review your bid optimization results and apply them to your campaigns.
        </p>

        <Card className="mb-6">
          <div className="mb-4 flex justify-between items-center">
            <span className="text-sm font-medium text-gray-800">
              Selected Rows: {selectedRowKeys.length}
            </span>
            <div>
              <Button type="primary" onClick={handleSelectAll} className="mr-2">
                Select All
              </Button>
              <Button onClick={handleClearAll}>Clear All</Button>
            </div>
          </div>

          {selectedFilters.length > 0 && (
            <div className="mb-4">
              <span className="text-sm font-medium text-gray-800 mr-2">
                Applied Filters:
              </span>
              <Space size="small" wrap>
                {selectedFilters.map((filter) => (
                  <Tag
                    key={filter.key}
                    closable
                    onClose={() => removeFilter(filter.key)}
                    color="blue"
                  >
                    {filter.column}: {filter.value}
                  </Tag>
                ))}
              </Space>
            </div>
          )}

          <Table
            key={tableKey}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys,
              onChange: onSelectChange,
            }}
            columns={[
              {
                title: "Sr.No.",
                dataIndex: "key",
                key: "srNo",
                width: 80,
                render: (text) => <span>{text + 1}</span>,
                fixed: "left",
              },
              ...columnsWithFilters,
            ]}
            dataSource={visibleBidsRecords}
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 50 }}
            bordered
            rowKey={(record, index) => record.key || index}
            onChange={handleTableChange}
          />
        </Card>

        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" className="mb-6">
          <TabPane tab="Facebook" key="facebook">
            <Card bordered={false}>
              <h4 className="text-base font-semibold text-gray-900 mb-4">
                Facebook Bid Optimizer
              </h4>
              <Table
                columns={[
                  { title: "Adset Type", dataIndex: "type", key: "type" },
                  { title: "Adset ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Adset ID"
                        name="testAdsetId"
                        value={formData.testAdsetId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="testBudget"
                        value={formData.testBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button type="primary" onClick={onApplyBids} loading={facebookLoading}>
                  {facebookLoading ? "Applying Bids..." : "Apply Facebook Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Google" key="google">
            <Card bordered={false}>
              <h4 className="text-base font-semibold text-gray-900 mb-4">
                Google Bid Optimizer
              </h4>
              <Table
                columns={[
                  { title: "Campaign Type", dataIndex: "type", key: "type" },
                  { title: "Campaign ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Campaign ID"
                        name="googleTestCampaignId"
                        value={formData.googleTestCampaignId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="googleTestBudget"
                        value={formData.googleTestBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button type="primary" onClick={onGoogleApplyBids} loading={googleLoading}>
                  {googleLoading ? "Applying Bids..." : "Apply Google Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Universal Ads" key="universal_ads">
            <Card bordered={false}>
              <h4 className="text-base font-semibold text-gray-900 mb-4">
                Universal Ads Bid Optimizer
              </h4>
              <Table
                columns={[
                  { title: "Campaign Type", dataIndex: "type", key: "type" },
                  { title: "Campaign ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Campaign ID"
                        name="universalTestCampaignId"
                        value={formData.universalTestCampaignId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="universalTestBudget"
                        value={formData.universalTestBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button type="primary" onClick={onUniversalApplyBids} loading={universalLoading}>
                  {universalLoading ? "Applying Bids..." : "Apply Universal Ads Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>
        </Tabs>

        {appliedBids.length > 0 && (
          <div className="mt-2">
            <h4 className="text-base font-semibold text-gray-900 mb-3">Previously Applied</h4>
            <Table
              dataSource={appliedBids.map((bid, idx) => ({ ...bid, key: idx }))}
              pagination={false}
              bordered
              size="small"
              columns={[
                {
                  title: "Platform",
                  dataIndex: "provider",
                  key: "provider",
                  render: (p: string) => (
                    <Tag color={p === "google" ? "blue" : p === "meta" ? "geekblue" : "purple"}>
                      {PROVIDER_LABELS[p] || p}
                    </Tag>
                  ),
                },
                {
                  title: "Test Campaign",
                  key: "test",
                  render: (_: any, bid: any) => (
                    <div>
                      <div className="font-medium text-gray-800">{bid.testCampaignId}</div>
                      <div className="text-xs text-gray-500">${bid.testBudget}</div>
                    </div>
                  ),
                },
                {
                  title: "Rows Applied",
                  key: "rows",
                  align: "center" as const,
                  render: (_: any, bid: any) => bid.selectedRowIndices?.length ?? "-",
                },
                {
                  title: "Applied At",
                  dataIndex: "appliedAt",
                  key: "appliedAt",
                  // No args to toLocaleString() means "format in the
                  // viewer's own browser timezone" - correct as long as the
                  // backend's appliedAt string is unambiguously UTC (it now
                  // is, see job_config_service.py). timeZoneName makes that
                  // per-viewer conversion visible instead of just showing a
                  // bare clock time with no indication it's zone-converted.
                  render: (v: string) =>
                    v ? new Date(v).toLocaleString(undefined, { timeZoneName: "short" }) : "-",
                },
              ]}
            />
          </div>
        )}
      </Card>

      {isAlert && (
        <Alert
          message="Bid Applied Successfully"
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          className="fixed bottom-8 right-8"
          closable
        />
      )}

      <div className="flex justify-between">
        <Button size="large" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="primary" size="large" onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default StepApplyBid;
